import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type { EffectivenessReport, ApiResponse } from '../types';

/**
 * Fetch the unified Aisthetix Effectiveness report.
 * Mirrors useUsage: loading/error states, single fetch on mount.
 */
export function useAnalytics() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [report, setReport] = useState<EffectivenessReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/analytics/effectiveness');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<EffectivenessReport> = await response.json();

      if (json.success && json.data) {
        setReport(json.data);
      } else {
        throw new Error(json.error || 'Unknown API error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch analytics';
      setError(message);
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { report, loading, error, refetch: fetchData };
}
